<template>
  <div class="container">
    <Header />
    <div class="center">
      <div class="password-container">
        <div class="password-title">
          {{ passwordTitle }}
        </div>
        <PasswordField ref="pwField"
                      class="password-field"
                      @update:input="onPasswordInput"
                      @update:enter="onSubmit" />
        <div class="error-container">
          <div class="error-message">
            {{ message }}
          </div>
        </div>
        <Button ref="submitBtn" :config="{
          buttonText: $T('file_request', 'next'),
          defaultEnabled: false,
          width: 120,
          height: 30,
        }" class="submit-btn" @click="onSubmit" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import Header from '@/components/Header.vue'
import PasswordField from '@/components/PasswordField.vue'
import Button from '@/components/Button.vue'
import drivewebapi from '@/store/drivewebapi'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { AUTH_STATUS } from '@/utils/constant'
import { $T, formatString } from '@/utils/texts'
import { serverDriveFileRequestCreator } from '@/utils/serverInject'

const router = useRouter()
const store = useStore()
const creator = ref(serverDriveFileRequestCreator())
const message = ref('')
const pwField = ref(null)
const submitBtn = ref(null)

const updateAuthStatus = (status) => {
  store.commit('setAuthStatus', status)
}

const passwordTitle = computed(() => {
  return formatString($T('file_request', 'password_page_title'), creator.value)
})

const onSubmit = async (event) => {
  event.preventDefault();

  // get auth status
  const authStatus = computed(() => store.state.authStatus)

  if (authStatus.value === AUTH_STATUS.AUTHENTICATING || authStatus.value === AUTH_STATUS.DONE) {
    return
  }

  updateAuthStatus(AUTH_STATUS.AUTHENTICATING)

  try {
    await drivewebapi.authFileRequestPassword(pwField.value.getPassword())
    message.value = ''
    updateAuthStatus(AUTH_STATUS.DONE)

    router.push('/upload')
  } catch (e) {
    console.error('Failed to auth file request password', e)
    updateAuthStatus(AUTH_STATUS.ERROR)
    // DRIVE_ERR_FILE_REQ_LINK_PASSWORD
    if (e.code === 1056) {
      message.value = $T('file_request', 'invalid_password')
      pwField.value.setErrorState(true)
      submitBtn.value.setEnabled(false)
    } else if (e.code === 1057) { // DRIVE_ERR_FILE_REQ_LINK_EXPIRED
      message.value = $T('file_request', 'err_msg_expire')
    } else {
      message.value = ''
    }
  }
}

const onPasswordInput = (password) => {
  if (password.length > 0) {
    submitBtn.value.setEnabled(true)
  } else {
    submitBtn.value.setEnabled(false)
  }
  message.value = ''
}

</script>

<style lang="scss" scoped>
.container {
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  width: 100%;

  .center {
    position: absolute;
    height: 100%;
    display: flex;
    align-items: center;
  }
  .password-container {
    height: 152px;
    width: 480px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    .password-title {
      color: var(--Color-Font-color_font_tier1, #2E3032);
      text-align: center;
      font-size: 15px;
      font-style: normal;
      font-weight: 700;
      line-height: 24px; /* 160% */
    }
    .password-field {
      margin-top: 20px;
    }
    .error-container {
      display: flex;
      width: 240px;
      padding: 4px 0px;
      justify-content: center;
      align-items: flex-start;
      gap: 10px;

      .error-message {
        display: flex;
        min-height: 20px;
        flex-direction: column;
        justify-content: center;
        flex: 1 0 0;
        color: var(--Color-Status-color_red, #F2616D);
        font-feature-settings: 'liga' off, 'clig' off;
        font-size: 12px;
        font-style: normal;
        font-weight: 400;
        line-height: 20px;
      }
    }

    .submit-btn {
      margin-top: 20px;
      user-select: none;
      -webkit-user-select: none;
    }
  }
}

@media (max-width: 624px) {
  .container {
    .password-container {
      width: 100%;
    }
  }
}
</style>
